import React, { useState, useEffect } from 'react';
import { messagesApi } from '../services/api';
import { User } from '../contexts/AuthContext';
import useAudioAlerts from '../hooks/useAudioAlerts';

interface MessageNotificationProps {
  user: User;
  onNavigateToMessages: () => void;
  refresh?: number; // Prop para forzar actualización cuando cambie
}

const MessageNotification: React.FC<MessageNotificationProps> = ({ user, onNavigateToMessages, refresh }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [latestMessage, setLatestMessage] = useState<any>(null);

  // Hook para alertas de audio
  const { addSystemAlert } = useAudioAlerts({
    enableSound: true,
    soundVolume: 0.8,
    enableNotifications: true
  });

  useEffect(() => {
    loadUnreadCount();
    
    // Revisar mensajes cada 10 segundos
    const interval = setInterval(() => {
      checkForNewMessages();
    }, 10000);

    return () => clearInterval(interval);
  }, [user.id, refresh]); // Actualizar cuando cambie el prop refresh

  const loadUnreadCount = async () => {
    try {
      const response = await messagesApi.getUnreadCount();
      const newUnreadCount = response.data.data.unreadCount;
      
      
      // Si hay más mensajes no leídos que antes, mostrar alerta
      if (newUnreadCount > unreadCount && unreadCount >= 0) {
        setShowAlert(true);
        // Obtener el mensaje más reciente para la notificación
        loadLatestMessage();
        // Reproducir sonido de notificación
        addSystemAlert('📧 Nuevo mensaje recibido', 'medium');
      }
      
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Error cargando contador de mensajes:', error);
    }
  };

  const loadLatestMessage = async () => {
    try {
      const response = await messagesApi.getMyMessages();
      if (response.data.data && response.data.data.length > 0) {
        const latest = response.data.data[0]; // El más reciente
        if (!latest.leido) {
          setLatestMessage(latest);
        }
      }
    } catch (error) {
      console.error('Error cargando último mensaje:', error);
    }
  };

  const checkForNewMessages = async () => {
    const prevCount = unreadCount;
    await loadUnreadCount();
  };

  const handleNotificationClick = () => {
    setShowAlert(false);
    onNavigateToMessages();
  };

  const dismissAlert = () => {
    setShowAlert(false);
  };

  // Notificación flotante cuando llegan mensajes nuevos
  if (showAlert && latestMessage) {
    return (
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#1f2937',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          zIndex: 1000,
          maxWidth: '350px',
          cursor: 'pointer',
          border: '2px solid #3b82f6',
          animation: 'slideIn 0.3s ease-out'
        }}
        onClick={handleNotificationClick}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.2rem' }}>💬</span>
            <strong style={{ fontSize: '0.9rem' }}>Nuevo Mensaje</strong>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              dismissAlert();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '1.2rem',
              padding: '0'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{ fontSize: '0.85rem', color: '#e5e7eb' }}>
          <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
            De: {latestMessage.sender?.name || 'Usuario'}
          </div>
          <div style={{ 
            opacity: 0.8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {latestMessage.contenido}
          </div>
        </div>
        
        <div style={{ 
          marginTop: '0.75rem', 
          fontSize: '0.75rem', 
          color: '#3b82f6',
          fontWeight: '500' 
        }}>
          Hacer clic para ver mensajes →
        </div>

        <style>{`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  // Indicador de mensajes no leídos en el header/nav
  if (unreadCount > 0) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 1rem',
          backgroundColor: '#fef3c7',
          color: '#92400e',
          borderRadius: '20px',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          border: '1px solid #fbbf24'
        }}
        onClick={onNavigateToMessages}
      >
        <span>💬</span>
        <span>{unreadCount} mensaje{unreadCount !== 1 ? 's' : ''} nuevo{unreadCount !== 1 ? 's' : ''}</span>
        <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>→</span>
      </div>
    );
  }

  return null;
};

export default MessageNotification;